import type { TicketAuthor } from '../api/types.js';
import { useRevokeActor, useTrustedActors } from '../hooks/use-tickets.js';
import { timeAgo } from '../utils/time-ago.js';
import { Button } from './button.js';

// Who wrote in a ticket, and whom the operator trusts on the server (ADR-029). GitHub's association says how someone
// relates to the repository; only the operator's own decisions, recorded in the server's registry, make them trusted.

const ASSOCIATION: Record<string, string> = {
  OWNER: 'owner of the organisation',
  MEMBER: 'member of the organisation',
  COLLABORATOR: 'collaborator on the repository',
  CONTRIBUTOR: 'has contributed before',
  FIRST_TIME_CONTRIBUTOR: 'first contribution',
  FIRST_TIMER: 'first contribution on GitHub',
  NONE: 'no link to the repository',
};

function standing(a: TicketAuthor): { text: string; color: string } {
  if (a.operator) return { text: 'you', color: 'var(--color-text-3)' };
  if (a.bot) return { text: 'a bot, never trusted', color: 'var(--color-warn)' };
  if (a.trusted) return { text: 'trusted', color: 'var(--color-success)' };
  return { text: 'not trusted yet', color: 'var(--color-warn)' };
}

/** The authors, one per line: login, how GitHub relates them to the repository, and where they stand on this server */
export function TicketAuthors({ authors }: { authors: TicketAuthor[] }) {
  return (
    <ul className="space-y-1 text-sm" aria-label="Who wrote in the ticket">
      {authors.map((a) => {
        const s = standing(a);
        const relation = a.association ? ASSOCIATION[a.association] : undefined;
        return (
          <li key={a.login} className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <a href={`https://github.com/${a.login.replace(/\[bot\]$/, '')}`} target="_blank" rel="noreferrer" className="break-all hover:underline"
              style={{ color: 'var(--color-text)' }}>{a.login}</a>
            {relation && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{relation}</span>}
            <span className="ml-auto text-xs" style={{ color: s.color }}>{s.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** The server's trusted people, with Revoke: added only from Implement, when the operator trusts a ticket's authors */
export function TrustedPeople({ serverId }: { serverId: string }) {
  const { data, error } = useTrustedActors(serverId, true);
  const revoke = useRevokeActor(serverId);
  if (!data?.length && !error) return null;
  return (
    <details className="mt-4">
      <summary className="cursor-pointer py-1.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
        People you trust on this server{data ? ` (${data.length})` : ''}
      </summary>
      <p className="mt-1 max-w-[72ch] text-xs" style={{ color: 'var(--color-text-3)' }}>
        Added when you implement a ticket trusting its authors. Revoking someone only affects the tickets to come.
      </p>
      {error && <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>Could not load them: {(error as Error).message}</p>}
      <ul className="mt-2 space-y-1">
        {(data ?? []).map((a) => (
          <li key={a.ref} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="break-all" style={{ color: 'var(--color-text)' }}>{a.login}</span>
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
              trusted {timeAgo(a.decidedAt)}{a.via ? ` on ${a.via.replace(/^github:/, '')}` : ''}
            </span>
            <Button size="sm" variant="ghost" className="ml-auto max-lg:min-h-10" disabled={revoke.isPending && revoke.variables === a.ref}
              onClick={() => revoke.mutate(a.ref)} aria-label={`Revoke your trust in ${a.login}`}>
              {revoke.isPending && revoke.variables === a.ref ? 'Revoking…' : 'Revoke'}
            </Button>
          </li>
        ))}
      </ul>
      {revoke.error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not revoked: {(revoke.error as Error).message}</p>}
    </details>
  );
}
